import React, { useState, useEffect, useCallback, useMemo } from "react";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {
  PieChart, Pie, Cell, Tooltip as RTooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line,
} from "recharts";
import axios from "axios";
import {
  Box, Typography, Paper, Grid, Snackbar, Alert,
  List, ListItemButton, ListItemText, Collapse,
  CircularProgress, Fade, ToggleButtonGroup, ToggleButton,
  IconButton, Tooltip, Divider,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import FactoryIcon from "@mui/icons-material/Factory";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import RefreshIcon from "@mui/icons-material/Refresh";
import HistoryIcon from "@mui/icons-material/History";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useUser } from "./UserContext";

import { AdminThemeProvider, useAdminTheme } from "./admin/AdminThemeContext";
import { adminDarkTheme, adminLightTheme, T } from "./admin/adminTheme";
import { SidebarRoot, MainArea, NavItem, StatCard } from "./admin/shared";
import WorkstationsTab from "./admin/WorkstationsTab";
import EmployeesTab from "./admin/EmployeesTab";
import UsersTab from "./admin/UsersTab";
import ShiftLogsTab from "./admin/ShiftLogsTab";
import OperationLogsTab from "./admin/OperationLogsTab";
import JobSummaryTab from "./admin/JobSummaryTab";
import RecentShiftsTab from "./admin/RecentShiftsTab";
import SessionCleanupLogsTab from "./admin/SessionCleanupLogsTab";
import SessionRecoveryTab from "./admin/SessionRecoveryTab";
import UserLogOut from "./UserLogOut";
import ManhourSessionRecoveryPanel from "./admin/ManhourSessionRecoveryPanel";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const SIDEBAR_EXPANDED_WIDTH = 286;
const SIDEBAR_COLLAPSED_WIDTH = 82;
const SIDEBAR_COLLAPSED_STORAGE_KEY = "sc_admin_sidebar_collapsed";

// ─────────────────────────────────────────────────────────────────────────────
// Chart colours
// ─────────────────────────────────────────────────────────────────────────────

const CHART_COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444",
  "#8b5cf6", "#06b6d4", "#f97316", "#84cc16",
];

// ─────────────────────────────────────────────────────────────────────────────
// ChartCard
// ─────────────────────────────────────────────────────────────────────────────

function ChartCard({ title, subtitle, children, loading }) {
  const theme = useTheme();
  const t = T(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: "12px",
        p: 2.5,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: t.s1,
        border: `0.5px solid ${t.border}`,
        boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.4)" : "none",
        transition: "background 0.25s ease",
      }}
    >
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, color: t.heading, letterSpacing: "-0.01em" }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 11, color: t.muted, mt: 0.2, fontWeight: 500 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {loading
        ? <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={22} sx={{ color: t.blue }} />
        </Box>
        : children}
    </Paper>
  );
}

// Pie label
const RADIAN = Math.PI / 180;
function PieLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (percent < 0.06) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Nav groups
// ─────────────────────────────────────────────────────────────────────────────

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { key: "overview", label: "Overview", icon: <DashboardIcon sx={{ fontSize: 18 }} /> },
    ],
  },
  {
    label: "Master data",
    items: [
      { key: "workstation", label: "Workstations", icon: <PrecisionManufacturingIcon sx={{ fontSize: 18 }} /> },
      { key: "employee", label: "Employees", icon: <PersonAddIcon sx={{ fontSize: 18 }} /> },
      { key: "user", label: "User accounts", icon: <ManageAccountsIcon sx={{ fontSize: 18 }} /> },
    ],
  },
  {
    label: "Logs & records",
    items: [
      { key: "shiftlogs", label: "Shift logs", icon: <HistoryIcon sx={{ fontSize: 18 }} /> },
      { key: "operationlogs", label: "Operation logs", icon: <ListAltIcon sx={{ fontSize: 18 }} /> },
      { key: "jobsummary", label: "Job summary", icon: <AssignmentTurnedInIcon sx={{ fontSize: 18 }} /> },
      { key: "recentshifts", label: "Recent shifts", icon: <WatchLaterIcon sx={{ fontSize: 18 }} /> },
      { key: "cleanupLogs", label: "Cleanup logs", icon: <FactCheckIcon sx={{ fontSize: 18 }} /> },
    ],
  },
  {
    label: "Maintenance",
    items: [
      { key: "sessionRecovery", label: "Session recovery", icon: <CleaningServicesIcon sx={{ fontSize: 18 }} /> },
      { key: "manhourRecovery", label: "Man-hour recovery", icon: <CleaningServicesIcon sx={{ fontSize: 16 }} /> },
    ],
  },
];

const ALL_NAV = NAV_GROUPS.flatMap((g) => g.items);

// ─────────────────────────────────────────────────────────────────────────────
// ThemeToggleButton — premium pill toggle
// ─────────────────────────────────────────────────────────────────────────────

function ThemeToggleButton({ collapsed = false }) {
  const { isDark, toggleMode, sidebarColors } = useAdminTheme();

  return (
    <Tooltip title={isDark ? "Switch to light mode" : "Switch to dark mode"} placement="right" arrow>
      <Box
        onClick={toggleMode}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 0 : 1.7,
          py: 1.1,
          mx: 1.1,
          minHeight: collapsed ? 46 : "auto",
          borderRadius: "11px",
          cursor: "pointer",
          border: `0.5px solid ${sidebarColors.border}`,
          background: "rgba(255,255,255,0.08)",
          color: sidebarColors.text,
          transition: "all 0.22s ease",
          "&:hover": {
            background: sidebarColors.hover,
            borderColor: sidebarColors.activeIcon,
            transform: collapsed ? "translateY(-1px)" : "none",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: collapsed ? 0 : 1.1 }}>
          {isDark
            ? <LightModeIcon sx={{ fontSize: 16, color: sidebarColors.activeIcon }} />
            : <DarkModeIcon sx={{ fontSize: 16, color: sidebarColors.activeIcon }} />
          }
          {!collapsed && (
            <Typography sx={{ fontSize: 13.2, fontWeight: 700, color: sidebarColors.text }}>
              {isDark ? "Light mode" : "Dark mode"}
            </Typography>
          )}
        </Box>

        {!collapsed && (
          <Box sx={{
            width: 37,
            height: 20,
            borderRadius: "20px",
            background: "rgba(255,255,255,0.22)",
            border: `0.5px solid ${sidebarColors.border}`,
            position: "relative",
            transition: "background 0.25s ease",
            flexShrink: 0,
            boxShadow: "0 0 8px rgba(255,255,255,0.10)",
          }}>
            <Box sx={{
              position: "absolute",
              top: 2.25,
              left: isDark ? 19 : 2.25,
              width: 15.5,
              height: 15.5,
              borderRadius: "50%",
              background: "#ffffff",
              boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
              transition: "left 0.22s cubic-bezier(0.4,0,0.2,1)",
            }} />
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AdminDashboardInner
// ─────────────────────────────────────────────────────────────────────────────

function AdminDashboardInner() {
  const { username } = useUser();
  const theme = useTheme();
  const { sidebarColors } = useAdminTheme();  // ✅ Added sidebarColors
  const t = T(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [activeTab, setActiveTab] = useState("overview");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [stats, setStats] = useState({ machineGroups: null, machines: null, employees: null, users: null });
  const [sessionRecoveryFilter, setSessionRecoveryFilter] = useState(null);
  const [employeesSearch, setEmployeesSearch] = useState("");
  const [usersSearch, setUsersSearch] = useState("");
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsRange, setAnalyticsRange] = useState(30);
  const [rawMachines, setRawMachines] = useState([]);
  const [rawEmployees, setRawEmployees] = useState([]);
  const [rawUsers, setRawUsers] = useState([]);
  const [rawJobs, setRawJobs] = useState([]);
  const [rawOps, setRawOps] = useState([]);
  const [rawShifts, setRawShifts] = useState([]);
  const [manhourRecoveryFilter, setManhourRecoveryFilter] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const sidebarWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
      } catch (_) {}
      return next;
    });
  }, []);

  // ── Collapsible nav groups ────────────────────────────────────────────────
  // Find which group the current active tab belongs to
  const activeGroupLabel = useMemo(() =>
    NAV_GROUPS.find((g) => g.items.some((i) => i.key === activeTab))?.label || "",
    [activeTab]);

  // Start with the active group open; others collapsed
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const initial = {};
    NAV_GROUPS.forEach((g) => {
      initial[g.label] = g.items.some((i) => i.key === "overview")
        ? true   // Overview group open by default
        : g.items.some((i) => i.key === activeTab);
    });
    return initial;
  });

  // Auto-expand the group of the newly active tab (e.g. from Quick Access cards)
  useEffect(() => {
    if (activeGroupLabel) {
      setExpandedGroups((prev) => ({ ...prev, [activeGroupLabel]: true }));
    }
  }, [activeGroupLabel]);

  const toggleGroup = useCallback((label) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  }, []);

  const goToSessionRecovery = useCallback((filter) => {
    setSessionRecoveryFilter(filter);
    setActiveTab("sessionRecovery");
  }, []);

  const goToManhourRecovery = useCallback((filter) => {
    setManhourRecoveryFilter(filter);
    setActiveTab("manhourRecovery");
  }, []);

  const goToUsers = useCallback((empId) => {
    setUsersSearch(String(empId || ""));
    setActiveTab("user");
  }, []);

  const goToEmployees = useCallback((empId) => {
    setEmployeesSearch(String(empId || ""));
    setActiveTab("employee");
  }, []);

  const loadAnalytics = useCallback(async (days) => {
    setAnalyticsLoading(true);
    const endDate = new Date().toISOString().slice(0, 19);
    const startDate = new Date(Date.now() - days * 86400000).toISOString().slice(0, 19);
    try {
      const [mR, eR, uR, jR, oR, sR] = await Promise.allSettled([
        axios.get(`${API_BASE_URL}/admin/machines`),
        axios.get(`${API_BASE_URL}/admin/employees`),
        axios.get(`${API_BASE_URL}/admin/users`),
        axios.post(`${API_BASE_URL}/admin/jobsummary/search`, { startDate, endDate }),
        axios.post(`${API_BASE_URL}/admin/operationlogs/search`, { startDate, endDate }),
        axios.post(`${API_BASE_URL}/admin/shiftlogs/search`, { startDate, endDate }),
      ]);
      const machineList = mR.status === "fulfilled" ? (mR.value.data || []) : [];
      const employeeList = eR.status === "fulfilled" ? (eR.value.data || []) : [];
      const userList = uR.status === "fulfilled" ? (uR.value.data || []) : [];
      const jobList = jR.status === "fulfilled" ? (jR.value.data || []) : [];
      const opList = oR.status === "fulfilled" ? (oR.value.data || []) : [];
      const shiftList = sR.status === "fulfilled" ? (sR.value.data || []) : [];

      const distinctGroups = new Set(machineList.map((m) => m.MachineGroupCode).filter(Boolean)).size;
      setStats({
        machineGroups: distinctGroups || "—",
        machines: machineList.length || "—",
        employees: employeeList.length || "—",
        users: userList.length || "—",
      });
      setRawMachines(machineList);
      setRawEmployees(employeeList);
      setRawUsers(userList);
      setRawJobs(jobList);
      setRawOps(opList);
      setRawShifts(shiftList);
    } finally { setAnalyticsLoading(false); }
  }, []);

  useEffect(() => { loadAnalytics(analyticsRange); }, [analyticsRange, loadAnalytics]);

  // ── derived chart data ──────────────────────────────────────────────────────
  const machineStatusData = useMemo(() => {
    const active = rawMachines.filter((m) => Number(m.active_status) === 1).length;
    return [{ name: "Active", value: active }, { name: "Inactive", value: rawMachines.length - active }];
  }, [rawMachines]);

  const machineAvailData = useMemo(() => {
    const avail = rawMachines.filter((m) => Number(m.avaliability) === 1).length;
    return [{ name: "Available", value: avail }, { name: "In use", value: rawMachines.length - avail }];
  }, [rawMachines]);

  const userRoleData = useMemo(() => {
    const counts = {};
    rawUsers.forEach((u) => { counts[u.User_Category] = (counts[u.User_Category] || 0) + 1; });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [rawUsers]);

  const empByGroupData = useMemo(() => {
    const counts = {};
    rawEmployees.forEach((e) => { counts[e.MachineGroup] = (counts[e.MachineGroup] || 0) + 1; });
    return Object.entries(counts).map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count).slice(0, 10);
  }, [rawEmployees]);

  const prodVsWasteData = useMemo(() => {
    const p = rawJobs.reduce((s, j) => s + (Number(j.productionQty) || 0), 0);
    const w = rawJobs.reduce((s, j) => s + (Number(j.wasteQty) || 0), 0);
    const q = rawJobs.reduce((s, j) => s + (Number(j.PassQty) || 0), 0);
    return [
      { name: "Production", value: Math.round(p) },
      { name: "Waste", value: Math.round(w) },
      { name: "Pass", value: Math.round(q) },
    ].filter((d) => d.value > 0);
  }, [rawJobs]);

  const jobsByMachineData = useMemo(() => {
    const counts = {};
    rawJobs.forEach((j) => { counts[j.machineCode] = (counts[j.machineCode] || 0) + 1; });
    return Object.entries(counts).map(([name, jobs]) => ({ name, jobs }))
      .sort((a, b) => b.jobs - a.jobs).slice(0, 10);
  }, [rawJobs]);

  const jobsTrendData = useMemo(() => {
    const byDay = {};
    rawJobs.forEach((j) => {
      const day = (j.startDatetime || "").slice(0, 10);
      if (day) byDay[day] = (byDay[day] || 0) + 1;
    });
    return Object.entries(byDay).sort(([a], [b]) => a.localeCompare(b))
      .map(([date, jobs]) => ({ date: date.slice(5), jobs }));
  }, [rawJobs]);

  const timeBreakdownData = useMemo(() => {
    const byMachine = {};
    rawJobs.forEach((j) => {
      const mc = j.machineCode || "Unknown";
      if (!byMachine[mc]) byMachine[mc] = { name: mc, setup: 0, production: 0, idle: 0, meals: 0 };
      byMachine[mc].setup += Number(j.totalSetupTime) || 0;
      byMachine[mc].production += Number(j.totalProductionTime) || 0;
      byMachine[mc].idle += Number(j.totalIdleTime) || 0;
      byMachine[mc].meals += Number(j.totalMealsTime) || 0;
    });
    return Object.values(byMachine)
      .sort((a, b) => (b.production + b.setup + b.idle) - (a.production + a.setup + a.idle))
      .slice(0, 8);
  }, [rawJobs]);

  const opTypeData = useMemo(() => {
    const counts = {};
    rawOps.forEach((o) => { const t2 = o.operation_type || "Unknown"; counts[t2] = (counts[t2] || 0) + 1; });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [rawOps]);

  const avgDurationData = useMemo(() => {
    const sums = {}, cnts = {};
    rawOps.forEach((o) => {
      const t2 = o.operation_type || "Unknown";
      sums[t2] = (sums[t2] || 0) + (Number(o.duration_minutes) || 0);
      cnts[t2] = (cnts[t2] || 0) + 1;
    });
    return Object.entries(sums).map(([name, total]) => ({ name, avg: Math.round(total / cnts[name]) }))
      .sort((a, b) => b.avg - a.avg);
  }, [rawOps]);

  const hourlyActivityData = useMemo(() => {
    const hours = Array.from({ length: 24 }, (_, i) => ({ hour: `${String(i).padStart(2, "0")}:00`, ops: 0 }));
    rawOps.forEach((o) => {
      const h = new Date(o.start_datetime).getHours();
      if (!isNaN(h)) hours[h].ops++;
    });
    return hours;
  }, [rawOps]);

  const shiftStatusData = useMemo(() => {
    const active = rawShifts.filter((s) => s.ActiveStatus !== "Logged-OUT").length;
    return [{ name: "Active", value: active }, { name: "Logged out", value: rawShifts.length - active }];
  }, [rawShifts]);

  const shiftsByMachineData = useMemo(() => {
    const counts = {};
    rawShifts.forEach((s) => { counts[s.machineCode] = (counts[s.machineCode] || 0) + 1; });
    return Object.entries(counts).map(([name, shifts]) => ({ name, shifts }))
      .sort((a, b) => b.shifts - a.shifts).slice(0, 10);
  }, [rawShifts]);

  // ── helpers ─────────────────────────────────────────────────────────────────

  const onSnack = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const activeLabel = ALL_NAV.find((n) => n.key === activeTab)?.label || "";

  const axisStyle = { fontSize: 10, fill: t.muted };
  const tooltipStyle = {
    contentStyle: {
      background: t.s1,
      border: `0.5px solid ${t.border}`,
      borderRadius: 8,
      fontSize: 12,
      color: t.primary,
      boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.5)" : "0 4px 12px rgba(0,0,0,0.08)",
    },
    itemStyle: { color: t.primary },
  };

  const renderNavItem = useCallback((item) => {
    const isActive = activeTab === item.key;

    return (
      <Tooltip
        key={item.key}
        title={sidebarCollapsed ? item.label : ""}
        placement="right"
        arrow
        disableHoverListener={!sidebarCollapsed}
      >
        <ListItemButton
          disableRipple
          onClick={() => setActiveTab(item.key)}
          sx={{ p: 0, "&:hover": { background: "transparent" } }}
        >
          <NavItem
            active={isActive ? 1 : 0}
            sx={{
              flex: 1,
              minHeight: sidebarCollapsed ? 46 : 44,
              mx: sidebarCollapsed ? 1 : 1.1,
              px: sidebarCollapsed ? 0.65 : 1.15,
              py: sidebarCollapsed ? 0.55 : 0.65,
              borderRadius: "11px",
              justifyContent: sidebarCollapsed ? "center" : "flex-start",
              position: "relative",
              background: isActive ? sidebarColors.active : "transparent",
              borderColor: isActive ? sidebarColors.border : "transparent",
              boxShadow: isActive ? "inset 0 1px 0 rgba(255,255,255,0.08)" : "none",
              transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
              "&:hover": {
                background: isActive ? sidebarColors.activeStrong : sidebarColors.hover,
                transform: sidebarCollapsed ? "translateX(1px)" : "translateX(2px)",
              },
              "& .MuiListItemText-root": { m: 0 },
            }}
          >
            <Box sx={{
              width: sidebarCollapsed ? 34 : 31,
              height: sidebarCollapsed ? 34 : 31,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              background: isActive ? "rgba(255,255,255,0.16)" : "transparent",
              color: isActive ? sidebarColors.activeIcon : sidebarColors.icon,
              transition: "all 0.18s ease",
            }}>
              {item.icon}
            </Box>

            {!sidebarCollapsed && (
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: 14.85,
                  fontWeight: isActive ? 800 : 650,
                  color: isActive ? sidebarColors.activeText : sidebarColors.text,
                  letterSpacing: isActive ? "-0.01em" : "normal",
                }}
              />
            )}

            {isActive && (
              <Box sx={sidebarCollapsed ? {
                position: "absolute",
                right: 7,
                width: 5.5,
                height: 5.5,
                borderRadius: "50%",
                background: sidebarColors.activeIcon,
                boxShadow: `0 0 6px ${sidebarColors.activeIcon}`,
              } : {
                width: 5.5,
                height: 5.5,
                borderRadius: "50%",
                background: sidebarColors.activeIcon,
                flexShrink: 0,
                boxShadow: `0 0 6px ${sidebarColors.activeIcon}`,
              }} />
            )}
          </NavItem>
        </ListItemButton>
      </Tooltip>
    );
  }, [activeTab, sidebarCollapsed, sidebarColors]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: t.appBg, transition: "background 0.25s ease" }}>

      {/* ══════════════════════════════════════════════════════════════════════
          SIDEBAR — Premium redesign
      ══════════════════════════════════════════════════════════════════════ */}
      <SidebarRoot sx={{
        width: sidebarWidth,
        minWidth: sidebarWidth,
        maxWidth: sidebarWidth,
        flexBasis: sidebarWidth,
        background: sidebarColors.background,
        borderRight: `1px solid ${sidebarColors.border}`,
        transition: "width 0.25s ease, min-width 0.25s ease, max-width 0.25s ease",
        overflowX: "hidden",
      }}>

        {/* ── Logo area ─────────────────────────────────────────────────── */}
        <Box sx={{
          px: sidebarCollapsed ? 0.9 : 2.2,
          py: 2.2,
          borderBottom: `1px solid ${sidebarColors.divider}`,
          display: "flex",
          flexDirection: sidebarCollapsed ? "column" : "row",
          alignItems: "center",
          justifyContent: sidebarCollapsed ? "center" : "space-between",
          gap: sidebarCollapsed ? 1 : 1.65,
          flexShrink: 0,
          background: "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 60%)",
        }}>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: sidebarCollapsed ? 0 : 1.65,
            minWidth: 0,
          }}>
            <Box sx={{
              width: 38,
              height: 38,
              borderRadius: "11px",
              background: "linear-gradient(135deg, #ffffff 0%, #a0d8ff 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 2px 8px rgba(160,216,255,0.45), 0 0 0 1px rgba(160,216,255,0.25)",
            }}>
              <AdminPanelSettingsIcon sx={{ fontSize: 20, color: "#0078d4" }} />
            </Box>

            {!sidebarCollapsed && (
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{
                  fontWeight: 700,
                  fontSize: 14.5,
                  color: sidebarColors.text,
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                  whiteSpace: "nowrap",
                }}>
                  Admin panel
                </Typography>
              </Box>
            )}
          </Box>

          <Tooltip title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right" arrow>
            <IconButton
              size="small"
              onClick={toggleSidebarCollapsed}
              sx={{
                width: 30,
                height: 30,
                borderRadius: "9px",
                color: sidebarColors.text,
                border: `0.5px solid ${sidebarColors.border}`,
                background: "rgba(255,255,255,0.08)",
                flexShrink: 0,
                "&:hover": {
                  background: sidebarColors.hover,
                  color: sidebarColors.activeIcon,
                },
              }}
            >
              {sidebarCollapsed
                ? <ChevronRightIcon sx={{ fontSize: 18 }} />
                : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Navigation ────────────────────────────────────────────────── */}
        {/* Scroll container — constrained by SidebarRoot height: 100vh */}
        <Box sx={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}>
          {/* Scrollable nav list */}
          <Box sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            pt: sidebarCollapsed ? 0.9 : 0.7,
            pb: 1.1,
            scrollbarWidth: "thin",
            scrollbarColor: `rgba(255,255,255,0.18) transparent`,
            "&::-webkit-scrollbar": { width: 5 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 4,
              background: "rgba(255,255,255,0.18)",
              "&:hover": { background: "rgba(255,255,255,0.30)" },
            },
          }}>
            {NAV_GROUPS.map((group) => {
              const isOpen = !!expandedGroups[group.label];
              const groupActive = group.items.some((i) => i.key === activeTab);

              return (
                <Box key={group.label} sx={{ mb: sidebarCollapsed ? 0.25 : 0.4 }}>

                  {sidebarCollapsed && group.label !== "Overview" && (
                    <Box sx={{ mx: 2.4, my: 0.45, height: "0.5px", background: sidebarColors.divider, opacity: 0.45 }} />
                  )}

                  {!sidebarCollapsed && (
                    <Box
                      onClick={() => toggleGroup(group.label)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mx: 1.1,
                        mt: 1.1,
                        mb: 0.35,
                        px: 1.4,
                        py: 0.75,
                        borderRadius: "9px",
                        cursor: "pointer",
                        transition: "background 0.14s ease",
                        userSelect: "none",
                        background: !isOpen && groupActive ? sidebarColors.active : "transparent",
                        "&:hover": {
                          background: sidebarColors.hover,
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
                        <Box sx={{
                          width: 4.5,
                          height: 4.5,
                          borderRadius: "50%",
                          flexShrink: 0,
                          background: groupActive ? sidebarColors.activeIcon : sidebarColors.textSecondary,
                          opacity: groupActive ? 1 : 0.4,
                          transition: "all 0.18s ease",
                          boxShadow: groupActive ? `0 0 5px ${sidebarColors.activeIcon}` : "none",
                        }} />

                        <Typography sx={{
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: "0.10em",
                          textTransform: "uppercase",
                          color: groupActive ? sidebarColors.activeIcon : sidebarColors.textSecondary,
                          transition: "color 0.15s ease",
                        }}>
                          {group.label}
                        </Typography>

                        <Box sx={{
                          px: 0.85,
                          py: 0.05,
                          borderRadius: "11px",
                          background: "rgba(255, 255, 255, 0.1)",
                          border: `0.5px solid ${sidebarColors.border}`,
                          lineHeight: "16px",
                          minWidth: 20,
                          textAlign: "center",
                          opacity: isOpen ? 0 : 1,
                          transition: "opacity 0.15s ease",
                        }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 800, color: sidebarColors.textSecondary, lineHeight: "18px" }}>
                            {group.items.length}
                          </Typography>
                        </Box>
                      </Box>

                      <KeyboardArrowDownIcon sx={{
                        fontSize: 16,
                        color: sidebarColors.textSecondary,
                        transition: "transform 0.22s cubic-bezier(0.4,0,0.2,1), color 0.15s ease",
                        transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                        ...(groupActive && { color: sidebarColors.activeIcon }),
                      }} />
                    </Box>
                  )}

                  {sidebarCollapsed ? (
                    <List sx={{ px: 0, pt: 0.05, pb: 0.2 }}>
                      {group.items.map(renderNavItem)}
                    </List>
                  ) : (
                    <Collapse in={isOpen} timeout={200} unmountOnExit>
                      <List sx={{ px: 0, pt: 0.1, pb: 0.35 }}>
                        {group.items.map(renderNavItem)}
                      </List>
                    </Collapse>
                  )}

                  {!sidebarCollapsed && isOpen && (
                    <Box sx={{ mx: 2.2, mt: 0.6, height: "0.5px", background: sidebarColors.divider, opacity: 0.55 }} />
                  )}
                </Box>
              );
            })}

            {/* Bottom padding */}
            <Box sx={{ height: 10 }} />
          </Box>

          {/* Scroll-fade overlay */}
          <Box sx={{
            position: "absolute",
            bottom: 0, left: 0, right: 0,
            height: 31,
            pointerEvents: "none",
            background: "linear-gradient(to bottom, transparent, rgba(0, 90, 158, 0.86))",
          }} />
        </Box>

        {/* ── Theme toggle + user footer ─────────────────────────────────── */}
        <Box sx={{ pb: 1.65, flexShrink: 0 }}>
          <Divider sx={{ mb: 1.4, opacity: 0.55, borderColor: sidebarColors.divider }} />

          {/* Theme toggle */}
          <ThemeToggleButton collapsed={sidebarCollapsed} />

          {/* User card */}
          <Box sx={{ px: sidebarCollapsed ? 1 : 1.65, mt: 1.1 }}>
            {sidebarCollapsed ? (
              <Tooltip title={`${username || "Admin"} - Logged in`} placement="right" arrow>
                <Box sx={{
                  height: 46,
                  borderRadius: "11px",
                  background: "rgba(255,255,255,0.04)",
                  border: `0.5px solid ${sidebarColors.divider}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "default",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
                }}>
                  <Box sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "8px",
                    background: "rgba(255,255,255,0.15)",
                    border: `0.5px solid ${sidebarColors.activeIcon}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                    <Typography sx={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: sidebarColors.activeIcon,
                      lineHeight: 1,
                      fontFamily: "monospace",
                    }}>
                      {String(username || "A").slice(0, 2).toUpperCase()}
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
            ) : (
              <Box sx={{
                borderRadius: "11px",
                p: "11px 13px",
                background: "rgba(255,255,255,0.04)",
                border: `0.5px solid ${sidebarColors.divider}`,
                position: "relative",
                overflow: "hidden",
                borderLeft: `2.5px solid ${sidebarColors.activeIcon}`,
              }}>
                <Box sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: 66,
                  height: 66,
                  background: "radial-gradient(circle, rgba(160,216,255,0.08) 0%, transparent 70%)",
                  pointerEvents: "none",
                }} />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                  <Box sx={{
                    width: 29,
                    height: 29,
                    borderRadius: "8px",
                    background: "rgba(255,255,255,0.15)",
                    border: `0.5px solid ${sidebarColors.activeIcon}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <Typography sx={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: sidebarColors.activeIcon,
                      lineHeight: 1,
                      fontFamily: "monospace",
                    }}>
                      {String(username || "A").slice(0, 2).toUpperCase()}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{
                      color: sidebarColors.text,
                      fontWeight: 700,
                      fontSize: 13.2,
                      lineHeight: 1.2,
                      letterSpacing: "-0.01em",
                    }}>
                      {username || "Admin"}
                    </Typography>
                    <Typography sx={{
                      color: sidebarColors.textSecondary,
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: "0.04em",
                    }}>
                      LOGGED IN
                    </Typography>
                  </Box>
                </Box>
                <UserLogOut username={username} />
              </Box>
            )}
          </Box>
        </Box>
      </SidebarRoot>

      {/* ══════════════════════════════════════════════════════════════════════
          MAIN AREA
      ══════════════════════════════════════════════════════════════════ */}
      <MainArea sx={{
        marginLeft: `${sidebarWidth}px`,
        width: `calc(100% - ${sidebarWidth}px)`,
        maxWidth: `calc(100% - ${sidebarWidth}px)`,
        flex: 1,
        transition: "margin-left 0.25s ease, width 0.25s ease, max-width 0.25s ease",
      }}>

        {/* ── Page header ─────────────────────────────────────────────── */}
        <Box sx={{ mb: 3.5 }}>
          {/* Breadcrumb-style context label */}
          <Typography sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: t.blue,
            mb: 0.5,
            opacity: 0.85,
          }}>
            {activeTab === "overview"
              ? "Dashboard"
              : NAV_GROUPS.find((g) => g.items.some((i) => i.key === activeTab))?.label || ""}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
            <Typography sx={{
              fontSize: 26,
              fontWeight: 700,
              color: t.heading,
              letterSpacing: "-0.03em",
              lineHeight: 1,
            }}>
              {activeTab === "overview" ? "Overview" : activeLabel}
            </Typography>
            {/* Accent underline bar */}
            <Box sx={{
              mb: "3px",
              height: 3,
              width: 32,
              borderRadius: "3px",
              background: isDark
                ? `linear-gradient(90deg, ${t.blue}, transparent)`
                : `linear-gradient(90deg, ${t.blue}, transparent)`,
              opacity: 0.7,
            }} />
          </Box>

        </Box>

        {/* ── Overview tab ─────────────────────────────────────────────── */}
        {activeTab === "overview" && (
          <Fade in>
            <Box>
              {/* Stat cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard label="Machine groups" value={stats.machineGroups} color={t.blue} icon={<FactoryIcon sx={{ fontSize: 20 }} />} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard label="Workstations" value={stats.machines} color="#0ea5e9" icon={<PrecisionManufacturingIcon sx={{ fontSize: 20 }} />} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard label="Employees" value={stats.employees} color={t.green} icon={<PersonAddIcon sx={{ fontSize: 20 }} />} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard label="User accounts" value={stats.users} color={t.violet} icon={<ManageAccountsIcon sx={{ fontSize: 20 }} />} />
                </Grid>
              </Grid>

              {/* Analytics header */}
              <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                pb: 1.5,
                borderBottom: `0.5px solid ${t.border}`,
              }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: t.heading, letterSpacing: "-0.01em" }}>
                    Analytics
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: t.muted, mt: 0.2 }}>
                    Production data over the selected period
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
                  <Typography sx={{ fontSize: 11, color: t.muted, fontWeight: 500 }}>Period:</Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={analyticsRange}
                    onChange={(_, v) => v && setAnalyticsRange(v)}
                  >
                    {[7, 30, 90].map((d) => (
                      <ToggleButton key={d} value={d} sx={{ fontSize: 11, px: 1.5, py: 0.5, fontWeight: 600 }}>
                        {d}d
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <IconButton
                    size="small"
                    onClick={() => loadAnalytics(analyticsRange)}
                    sx={{
                      border: `0.5px solid ${t.border}`,
                      borderRadius: "8px",
                      width: 30,
                      height: 30,
                      "&:hover": { transform: "rotate(180deg)", transition: "transform 0.4s ease" },
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: 14, color: t.muted }} />
                  </IconButton>
                </Box>
              </Box>

              {/* ── Section: Workstations ─────────────────────────── */}
              <SectionDivider label="Workstations" t={t} />
              <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <ChartCard title="Machine status" subtitle="Active vs inactive" loading={analyticsLoading}>
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie data={machineStatusData} cx="50%" cy="50%" innerRadius={44} outerRadius={66} dataKey="value" labelLine={false} label={<PieLabel />}>
                          {machineStatusData.map((_, i) => <Cell key={i} fill={[t.blue, t.border][i]} />)}
                        </Pie>
                        <RTooltip {...tooltipStyle} />
                        <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: 11, color: t.secondary }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ChartCard title="Machine availability" subtitle="In use vs free" loading={analyticsLoading}>
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie data={machineAvailData} cx="50%" cy="50%" innerRadius={44} outerRadius={66} dataKey="value" labelLine={false} label={<PieLabel />}>
                          {machineAvailData.map((_, i) => <Cell key={i} fill={[t.green, t.amber][i]} />)}
                        </Pie>
                        <RTooltip {...tooltipStyle} />
                        <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: 11, color: t.secondary }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
                <Grid item xs={12} md={4}>
                  <ChartCard title="Employees by group" subtitle="Headcount per machine group" loading={analyticsLoading}>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={empByGroupData} layout="vertical" margin={{ left: 0, right: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={t.s3} />
                        <XAxis type="number" tick={axisStyle} />
                        <YAxis type="category" dataKey="name" tick={axisStyle} width={64} />
                        <RTooltip {...tooltipStyle} />
                        <Bar dataKey="count" name="Employees" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
              </Grid>

              {/* ── Section: User accounts ────────────────────────── */}
              <SectionDivider label="User accounts" t={t} />
              <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
                <Grid item xs={12} sm={5}>
                  <ChartCard title="Users by role" subtitle="Distribution by category" loading={analyticsLoading}>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={userRoleData} margin={{ left: 0, right: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={t.s3} />
                        <XAxis dataKey="name" tick={axisStyle} />
                        <YAxis tick={axisStyle} />
                        <RTooltip {...tooltipStyle} />
                        <Bar dataKey="value" name="Users" radius={[4, 4, 0, 0]}>
                          {userRoleData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <ChartCard title="Shift status" subtitle={`Active vs logged-out — last ${analyticsRange} days`} loading={analyticsLoading}>
                    <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
                      <Grid item xs={6}>
                        <ResponsiveContainer width="100%" height={140}>
                          <PieChart>
                            <Pie data={shiftStatusData} cx="50%" cy="50%" innerRadius={38} outerRadius={58} dataKey="value" labelLine={false} label={<PieLabel />}>
                              {shiftStatusData.map((_, i) => <Cell key={i} fill={[t.amber, t.muted][i]} />)}
                            </Pie>
                            <RTooltip {...tooltipStyle} />
                            <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: 11, color: t.secondary }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Grid>
                      <Grid item xs={6} sx={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 1 }}>
                        {shiftStatusData.map((d, i) => (
                          <Box key={d.name} sx={{
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            p: "8px 12px", borderRadius: "9px",
                            background: [t.s3, t.s2][i], border: `0.5px solid ${t.border}`,
                          }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: [t.amber, t.secondary][i] }}>{d.name}</Typography>
                            <Typography sx={{ fontSize: 18, fontWeight: 700, color: [t.amber, t.secondary][i] }}>{d.value}</Typography>
                          </Box>
                        ))}
                        <Typography sx={{ fontSize: 10, color: t.muted, fontWeight: 500 }}>
                          Total: {rawShifts.length} shifts
                        </Typography>
                      </Grid>
                    </Grid>
                  </ChartCard>
                </Grid>
              </Grid>

              {/* ── Section: Job summary ──────────────────────────── */}
              <SectionDivider label="Job summary" t={t} />
              <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <ChartCard title="Production vs waste" subtitle={`Qty breakdown — last ${analyticsRange} days`} loading={analyticsLoading}>
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie data={prodVsWasteData} cx="50%" cy="50%" innerRadius={44} outerRadius={66} dataKey="value" labelLine={false} label={<PieLabel />}>
                          {prodVsWasteData.map((_, i) => <Cell key={i} fill={[t.green, t.red, t.blue][i]} />)}
                        </Pie>
                        <RTooltip {...tooltipStyle} formatter={(v) => v.toLocaleString()} />
                        <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: 11, color: t.secondary }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ChartCard title="Jobs by machine" subtitle="Top machines by job count" loading={analyticsLoading}>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={jobsByMachineData} layout="vertical" margin={{ left: 0, right: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={t.s3} />
                        <XAxis type="number" tick={axisStyle} />
                        <YAxis type="category" dataKey="name" tick={axisStyle} width={64} />
                        <RTooltip {...tooltipStyle} />
                        <Bar dataKey="jobs" name="Jobs" fill={t.violet} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
                <Grid item xs={12} md={4}>
                  <ChartCard title="Jobs over time" subtitle="Daily completion trend" loading={analyticsLoading}>
                    <ResponsiveContainer width="100%" height={160}>
                      <LineChart data={jobsTrendData} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={t.s3} />
                        <XAxis dataKey="date" tick={axisStyle} interval="preserveStartEnd" />
                        <YAxis tick={axisStyle} />
                        <RTooltip {...tooltipStyle} />
                        <Line type="monotone" dataKey="jobs" name="Jobs" stroke={t.blue} strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
              </Grid>

              {/* Time breakdown */}
              <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
                <Grid item xs={12}>
                  <ChartCard title="Time breakdown per machine" subtitle="Setup / Production / Idle / Meals (minutes)" loading={analyticsLoading}>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={timeBreakdownData} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={t.s3} />
                        <XAxis dataKey="name" tick={axisStyle} />
                        <YAxis tick={axisStyle} />
                        <RTooltip {...tooltipStyle} />
                        <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: 11, color: t.secondary }} />
                        <Bar dataKey="production" name="Production" stackId="a" fill={t.green} />
                        <Bar dataKey="setup" name="Setup" stackId="a" fill={t.blue} />
                        <Bar dataKey="idle" name="Idle" stackId="a" fill={t.amber} />
                        <Bar dataKey="meals" name="Meals" stackId="a" fill={t.muted} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
              </Grid>

              {/* ── Section: Operation logs ───────────────────────── */}
              <SectionDivider label="Operation logs" t={t} />
              <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <ChartCard title="Operation type breakdown" subtitle={`Last ${analyticsRange} days`} loading={analyticsLoading}>
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie data={opTypeData} cx="50%" cy="50%" innerRadius={44} outerRadius={66} dataKey="value" labelLine={false} label={<PieLabel />}>
                          {opTypeData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Pie>
                        <RTooltip {...tooltipStyle} />
                        <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: 11, color: t.secondary }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <ChartCard title="Avg duration per op type" subtitle="Minutes per operation type" loading={analyticsLoading}>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={avgDurationData} layout="vertical" margin={{ left: 0, right: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={t.s3} />
                        <XAxis type="number" tick={axisStyle} unit=" min" />
                        <YAxis type="category" dataKey="name" tick={axisStyle} width={80} />
                        <RTooltip {...tooltipStyle} formatter={(v) => `${v} min`} />
                        <Bar dataKey="avg" name="Avg (min)" radius={[0, 4, 4, 0]}>
                          {avgDurationData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
                <Grid item xs={12} md={4}>
                  <ChartCard title="Activity by hour" subtitle="Operations started per hour of day" loading={analyticsLoading}>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={hourlyActivityData} margin={{ left: 0, right: 4, top: 4, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={t.s3} />
                        <XAxis dataKey="hour" tick={{ ...axisStyle, fontSize: 9 }} interval={3} />
                        <YAxis tick={axisStyle} />
                        <RTooltip {...tooltipStyle} />
                        <Bar dataKey="ops" name="Operations" fill="#06b6d4" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
              </Grid>

              {/* ── Section: Shift logs ───────────────────────────── */}
              <SectionDivider label="Shift logs" t={t} />
              <Grid container spacing={1.75} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <ChartCard title="Shifts per machine" subtitle={`Total shift count by machine — last ${analyticsRange} days`} loading={analyticsLoading}>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={shiftsByMachineData} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={t.s3} />
                        <XAxis dataKey="name" tick={axisStyle} />
                        <YAxis tick={axisStyle} />
                        <RTooltip {...tooltipStyle} />
                        <Bar dataKey="shifts" name="Shifts" radius={[4, 4, 0, 0]}>
                          {shiftsByMachineData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </Grid>
              </Grid>

              {/* ── Quick access ──────────────────────────────────── */}
              <Paper sx={{
                borderRadius: "14px",
                p: 2.5,
                background: t.s1,
                border: `0.5px solid ${t.border}`,
                boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.4)" : "none",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: t.heading, letterSpacing: "-0.01em" }}>
                    Quick access
                  </Typography>
                  <Box sx={{
                    height: 1,
                    flex: 1,
                    background: `linear-gradient(90deg, ${t.border}, transparent)`,
                  }} />
                </Box>
                <Grid container spacing={1.5}>
                  {[
                    { label: "Workstations", desc: "View & manage workstations", icon: <FactoryIcon sx={{ fontSize: 18 }} />, tab: "workstation", color: t.blue },
                    { label: "Employees", desc: "View & manage employees", icon: <PersonAddIcon sx={{ fontSize: 18 }} />, tab: "employee", color: t.green },
                    { label: "User accounts", desc: "View & manage system access", icon: <ManageAccountsIcon sx={{ fontSize: 18 }} />, tab: "user", color: t.violet },
                    { label: "Shift logs", desc: "Search and manage shift logs", icon: <HistoryIcon sx={{ fontSize: 18 }} />, tab: "shiftlogs", color: t.amber },
                    { label: "Operation logs", desc: "Search and manage operation logs", icon: <ListAltIcon sx={{ fontSize: 18 }} />, tab: "operationlogs", color: "#06b6d4" },
                    { label: "Job summary", desc: "Search and manage job summary", icon: <AssignmentTurnedInIcon sx={{ fontSize: 18 }} />, tab: "jobsummary", color: t.red },
                    { label: "Recent shifts", desc: "Drill into shifts with detail", icon: <WatchLaterIcon sx={{ fontSize: 18 }} />, tab: "recentshifts", color: t.blue },
                    { label: "Session recovery", desc: "Clean stale sessions", icon: <CleaningServicesIcon sx={{ fontSize: 18 }} />, tab: "sessionRecovery", color: t.violet },
                    { label: "Man-hour recovery", desc: "Clean man-hour sessions", icon: <CleaningServicesIcon sx={{ fontSize: 18 }} />, tab: "manhourRecovery", color: t.green },
                  ].map((a) => (
                    <Grid item xs={12} sm={6} md={3} key={a.tab}>
                      <Box
                        onClick={() => setActiveTab(a.tab)}
                        sx={{
                          p: 1.75,
                          borderRadius: "10px",
                          cursor: "pointer",
                          background: t.s2,
                          border: `0.5px solid ${t.border}`,
                          transition: "all 0.18s cubic-bezier(0.4,0,0.2,1)",
                          "&:hover": {
                            background: t.s3,
                            borderColor: a.color,
                            transform: "translateY(-2px)",
                            boxShadow: isDark
                              ? `0 4px 16px rgba(0,0,0,0.3), 0 0 0 0.5px ${a.color}30`
                              : `0 4px 12px rgba(0,0,0,0.06), 0 0 0 0.5px ${a.color}25`,
                          },
                        }}
                      >
                        <Box sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          background: `${a.color}18`,
                          border: `0.5px solid ${a.color}25`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: a.color,
                          mb: 1.25,
                        }}>
                          {a.icon}
                        </Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: t.heading, letterSpacing: "-0.01em" }}>
                          {a.label}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: t.muted, mt: 0.3, fontWeight: 500 }}>
                          {a.desc}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>
          </Fade>
        )}

        {/* ── Tab content ──────────────────────────────────────────────── */}
        {activeTab === "workstation" && (
          <WorkstationsTab
            onSnack={onSnack}
            adminUsername={username}
            goToSessionRecovery={goToSessionRecovery}
            goToManhourRecovery={goToManhourRecovery}
          />
        )}

        {activeTab === "employee" && (
          <EmployeesTab
            onSnack={onSnack}
            adminUsername={username}
            goToUsers={goToUsers}
            goToSessionRecovery={goToSessionRecovery}
            goToManhourRecovery={goToManhourRecovery}
            externalSearch={employeesSearch}
            setExternalSearch={setEmployeesSearch}
          />
        )}

        {activeTab === "user" && (
          <UsersTab
            onSnack={onSnack}
            adminUsername={username}
            goToEmployees={goToEmployees}
            goToSessionRecovery={goToSessionRecovery}
            externalSearch={usersSearch}
            setExternalSearch={setUsersSearch}
          />
        )}

        {activeTab === "shiftlogs" && <ShiftLogsTab onSnack={onSnack} />}
        {activeTab === "operationlogs" && <OperationLogsTab onSnack={onSnack} />}
        {activeTab === "jobsummary" && <JobSummaryTab onSnack={onSnack} />}
        {activeTab === "recentshifts" && <RecentShiftsTab onSnack={onSnack} />}
        {activeTab === "cleanupLogs" && <SessionCleanupLogsTab onSnack={onSnack} />}

        {activeTab === "sessionRecovery" && (
          <SessionRecoveryTab
            onSnack={onSnack}
            adminUsername={username}
            initialFilter={sessionRecoveryFilter}
            onInitialFilterApplied={() => setSessionRecoveryFilter(null)}
          />
        )}

        {activeTab === "manhourRecovery" && (
          <ManhourSessionRecoveryPanel
            currentAdminId={username}
            initialFilter={manhourRecoveryFilter}
            onInitialFilterApplied={() => setManhourRecoveryFilter(null)}
          />
        )}



        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={snackbar.severity}
            variant="filled"
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </MainArea>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SectionDivider — analytics section heading with a colored left bar
// ─────────────────────────────────────────────────────────────────────────────

function SectionDivider({ label, t }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.25, mt: 0.5 }}>
      <Box sx={{ width: 3, height: 14, borderRadius: "3px", background: t.blue, opacity: 0.7 }} />
      <Typography sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        color: t.muted,
      }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: "0.5px", background: t.border }} />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Root export
// ─────────────────────────────────────────────────────────────────────────────

function ThemedAdminDashboard() {
  const { mode } = useAdminTheme();
  const muiTheme = mode === "dark" ? adminDarkTheme : adminLightTheme;
  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <AdminDashboardInner />
    </ThemeProvider>
  );
}

export default function AdminDashboard() {
  return (
    <AdminThemeProvider>
      <ThemedAdminDashboard />
    </AdminThemeProvider>
  );
}